//==================================================================================================
// TrackingContainersTable
//
// Responsibilities:
// - Receives "trackingDetails" array where each item represents one ocean container tracking.
// - Renders a provider-aware containers timeline table.
// - Reuses TrackingEventsTable CSS classes so the styling remains aligned with the movements table.
// - Supports client-side pagination using PrimeReact Paginator.
//
// Provider behavior:
// - Shipsgo/SeaRates-like payloads keep the existing lifecycle columns:
//     Container | Empty to Shipper | Gate In | Gate Out | Empty Return
//
// - OpenSyllo payloads use DCSA/OpenSyllo equipment event columns:
//     Container | Discharged | Inspected | Drop-off | Loaded | Departure | Arrival
//
// Why provider-aware:
// - OpenSyllo does not return the same event codes used by Shipsgo.
// - Existing providers use codes such as EMSH, GTIN, GTOT, EMRT.
// - OpenSyllo returns DCSA-like codes such as DISC, INSP, DROP, LOAD.
// - Forcing OpenSyllo into the Shipsgo lifecycle columns would produce empty date cells.
//==================================================================================================

import React, { useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Paginator } from "primereact/paginator";

import { headerCellStyle, cellStyle as tdStyle } from "src/components/tableHelpers/TableStyleUtils";

import "./TrackingEventsTable.css"; // reuse same CSS

export default function TrackingContainersTable({ trackingDetails }) {
    const { t } = useTranslation();

    // #region Pagination state (client-side)

    /**
     * PrimeReact paginator "first" record index.
     */
    const [first, setFirst] = useState(0);

    /**
     * Number of table rows displayed per page.
     */
    const [rowsPerPage, setRowsPerPage] = useState(10);

    /**
     * Handles page and page-size changes emitted by PrimeReact Paginator.
     */
    const onPageChange = useCallback((e) => {
        setFirst(e.first);
        setRowsPerPage(e.rows);
    }, []);

    // #endregion

    // #region Input normalization

    /**
     * Normalizes trackingDetails to an array.
     *
     * The table expects one item per container tracking.
     */
    const items = useMemo(() => {
        return Array.isArray(trackingDetails) ? trackingDetails : [];
    }, [trackingDetails]);

    /**
     * Detects whether the current payload should use OpenSyllo-specific columns.
     *
     * Primary signal:
     * - Provider === "OpenSyllo"
     *
     * Defensive signals:
     * - OpenSyllo metadata such as equipment_reference, equipment_iso_code or transport_equipment_type.
     * - OpenSyllo/DCSA equipment event codes such as DISC, INSP, DROP, LOAD.
     */
    const isOpenSylloPayload = useMemo(() => {
        if (!items.length) return false;

        return items.some((item) => {
            const provider = String(item?.Provider ?? item?.provider ?? "")
                .trim()
                .toUpperCase();

            if (provider === "OPENSYLLO") return true;

            const equipmentReference =
                getItemMeta(item, "equipment_reference") ??
                getItemMeta(item, "equipmentReference");

            const equipmentIsoCode =
                getItemMeta(item, "equipment_iso_code") ??
                getItemMeta(item, "equipmentIsoCode");

            const transportEquipmentType =
                getItemMeta(item, "transport_equipment_type") ??
                getItemMeta(item, "transportEquipmentType");

            if (hasValue(equipmentReference) || hasValue(equipmentIsoCode) || hasValue(transportEquipmentType)) {
                return true;
            }

            return getEvents(item).some((ev) => {
                const code = getEventCode(ev);
                return ["DISC", "INSP", "DROP", "LOAD", "DEPA", "DEP", "ARRV", "ARR"].includes(code);
            });
        });
    }, [items]);

    // #endregion

    // #region Provider-aware columns

    /**
     * Builds the effective table columns.
     *
     * Shipsgo/SeaRates-like providers:
     * - Keep the original container lifecycle columns.
     *
     * OpenSyllo:
     * - Use DCSA/OpenSyllo event-code columns.
     * - Only render OpenSyllo columns that actually exist in the payload.
     * - This avoids showing many empty columns when OpenSyllo only returned DISC/LOAD, for example.
     */
    const columns = useMemo(() => {
        if (isOpenSylloPayload) {
            const openSylloColumns = [
                {
                    key: "discharged",
                    label: t("Discharged") || "Discharged",
                    codes: ["DISC"],
                    className: "col-discharged",
                },
                {
                    key: "inspected",
                    label: t("Inspected") || "Inspected",
                    codes: ["INSP"],
                    className: "col-inspected",
                },
                {
                    key: "dropoff",
                    label: t("DropOff") || "Drop-off",
                    codes: ["DROP"],
                    className: "col-dropoff",
                },
                {
                    key: "loaded",
                    label: t("Loaded") || "Loaded",
                    codes: ["LOAD"],
                    className: "col-loaded",
                },
                {
                    key: "departure",
                    label: t("Departure") || "Departure",
                    codes: ["DEPA", "DEP"],
                    className: "col-departure",
                },
                {
                    key: "arrival",
                    label: t("Arrival") || "Arrival",
                    codes: ["ARRV", "ARR"],
                    className: "col-arrival",
                },
            ];

            const availableColumns = openSylloColumns.filter((col) =>
                items.some((item) => getEvents(item).some((ev) => col.codes.includes(getEventCode(ev))))
            );

            /**
             * Defensive fallback:
             * - If OpenSyllo payload is detected but no known event code is available,
             *   still render the core OpenSyllo columns instead of showing only Container.
             */
            return availableColumns.length > 0 ? availableColumns : openSylloColumns.slice(0, 4);
        }

        return [
            {
                key: "emptyToShipper",
                label: t("EmptyToShipper"),
                codes: ["EMSH"],
                className: "col-ets",
            },
            {
                key: "gateIn",
                label: t("GateIn"),
                codes: ["GTIN"],
                className: "col-gatein",
            },
            {
                key: "gateOut",
                label: t("GateOut"),
                codes: ["GTOT"],
                className: "col-gateout",
            },
            {
                key: "emptyReturn",
                label: t("EmptyReturn"),
                codes: ["EMRT"],
                className: "col-emptyreturn",
            },
        ];
    }, [items, isOpenSylloPayload, t]);

    // #endregion

    // #region Row building and paging

    /**
     * Builds one row per container tracking item.
     *
     * Each row stores:
     * - container number;
     * - optional badge with size/type/ISO information;
     * - dynamic date values based on the provider-aware column definitions.
     */
    const rows = useMemo(() => {
        const builtRows = items.map((item, index) => {
            const containerNo = resolveContainerNumber(item);
            const badge = buildContainerBadge(item);
            const events = getEvents(item);

            const values = {};

            columns.forEach((col) => {
                values[col.key] = pickEventDate(events, col.codes);
            });

            return {
                key: `${containerNo}-${index}`,
                containerNo,
                badge,
                values,
            };
        });

        builtRows.sort((a, b) => String(a.containerNo).localeCompare(String(b.containerNo)));

        return builtRows;
    }, [items, columns]);

    /**
     * Current page slice for rendering.
     */
    const pageRows = useMemo(() => {
        return rows.slice(first, first + rowsPerPage);
    }, [rows, first, rowsPerPage]);

    /**
     * Resets pagination when the dataset changes.
     *
     * This prevents the table from staying on an invalid page after refreshes,
     * provider changes or payload changes.
     */
    useEffect(() => {
        setFirst(0);
    }, [rows.length]);

    // #endregion

    // #region Render

    return (
        <div className="trackingEvents-wrapper">
            <div className="trackingEvents-table-container">
                <table className="trackingEvents">
                    <thead>
                        <tr>
                            <th style={headerCellStyle()} className="trackingEvents-header-cell col-container">
                                {t("Container")}
                            </th>

                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    style={headerCellStyle()}
                                    className={`trackingEvents-header-cell ${col.className ?? ""}`}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {pageRows.length === 0 ? (
                            <tr>
                                <td className="trackingEvents-cell" style={tdStyle()} colSpan={columns.length + 1}>
                                    {t("NoData") || "No data"}
                                </td>
                            </tr>
                        ) : (
                            pageRows.map((r) => (
                                <tr key={r.key}>
                                    <td className="trackingEvents-cell col-container" style={tdStyle()}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                            <span>{r.containerNo}</span>

                                            {r.badge && (
                                                <span
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        height: 22,
                                                        padding: "0 8px",
                                                        borderRadius: 4,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        background: "rgba(0,0,0,0.55)",
                                                        color: "white",
                                                    }}
                                                >
                                                    {r.badge}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {columns.map((col) => (
                                        <td
                                            key={col.key}
                                            className={`trackingEvents-cell ${col.className ?? ""}`}
                                            style={tdStyle()}
                                        >
                                            {r.values[col.key] ?? "-"}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Paginator
                first={first}
                rows={rowsPerPage}
                totalRecords={rows.length}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={onPageChange}
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            />
        </div>
    );

    // #endregion
}

TrackingContainersTable.propTypes = {
    /**
     * TrackingDetails array in Ocean mode.
     * Each item should represent one container tracking object.
     */
    trackingDetails: PropTypes.array,
};

//==================================================================================================
// Private helpers
//==================================================================================================

/**
 * Returns item-level metadata using both casing variants commonly found in responses:
 * - Metadata
 * - metadata
 *
 * @param {object} item Tracking item.
 * @param {string} key Metadata key.
 * @returns {any} Metadata value or undefined.
 */
function getItemMeta(item, key) {
    if (!item) return undefined;

    const a = item?.Metadata?.[key];
    if (a !== undefined) return a;

    return item?.metadata?.[key];
}

/**
 * Returns event-level metadata using both casing variants commonly found in responses:
 * - Metadata
 * - metadata
 *
 * @param {object} ev Tracking event.
 * @param {string} key Metadata key.
 * @returns {any} Metadata value or undefined.
 */
function getEventMeta(ev, key) {
    if (!ev) return undefined;

    const a = ev?.Metadata?.[key];
    if (a !== undefined) return a;

    return ev?.metadata?.[key];
}

/**
 * Returns an item's events array safely.
 *
 * @param {object} item Tracking item.
 * @returns {Array} Events array.
 */
function getEvents(item) {
    if (Array.isArray(item?.Events)) return item.Events;
    if (Array.isArray(item?.events)) return item.events;
    return [];
}

/**
 * Returns the normalized event code for a provider-agnostic event.
 *
 * Supported metadata variants:
 * - event_code
 * - eventCode
 * - equipment_event_type_code
 * - equipmentEventTypeCode
 *
 * @param {object} ev Tracking event.
 * @returns {string} Uppercase event code.
 */
function getEventCode(ev) {
    const code =
        getEventMeta(ev, "event_code") ??
        getEventMeta(ev, "eventCode") ??
        getEventMeta(ev, "equipment_event_type_code") ??
        getEventMeta(ev, "equipmentEventTypeCode") ??
        "";

    return String(code).trim().toUpperCase();
}

/**
 * Resolves the visible container number from the item.
 *
 * Priority:
 * - ExternalId
 * - ContainerNumber
 * - Metadata.container_number
 * - Metadata.equipment_reference
 *
 * @param {object} item Tracking item.
 * @returns {string} Container number or "-".
 */
function resolveContainerNumber(item) {
    const value =
        item?.ExternalId ??
        item?.externalId ??
        item?.ContainerNumber ??
        item?.containerNumber ??
        getItemMeta(item, "container_number") ??
        getItemMeta(item, "containerNumber") ??
        getItemMeta(item, "equipment_reference") ??
        getItemMeta(item, "equipmentReference") ??
        "-";

    const normalized = String(value).trim();

    return normalized || "-";
}

/**
 * Checks if a value is present and non-empty when treated as a string.
 *
 * @param {any} value Candidate value.
 * @returns {boolean} True when value has content.
 */
function hasValue(value) {
    if (value == null) return false;
    return String(value).trim().length > 0;
}

/**
 * Converts a timestamp value into milliseconds since epoch.
 *
 * @param {string|Date|number|null|undefined} value Timestamp value.
 * @returns {number} Milliseconds since epoch or 0 on invalid input.
 */
function safeTime(value) {
    if (!value) return 0;

    const d = new Date(value);
    const t = d.getTime();

    return Number.isNaN(t) ? 0 : t;
}

/**
 * Formats a timestamp as dd/MM/yyyy.
 *
 * UTC is used deliberately to avoid local timezone date shifts.
 *
 * @param {string|Date|number|null|undefined} iso Timestamp candidate.
 * @returns {string} Formatted date or "-".
 */
function fmtDateOnly(iso) {
    if (!iso) return "-";

    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "-";

        const dd = String(d.getUTCDate()).padStart(2, "0");
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        const yyyy = String(d.getUTCFullYear());

        return `${dd}/${mm}/${yyyy}`;
    } catch {
        return "-";
    }
}

/**
 * Picks the most recent event matching one of the accepted event codes.
 *
 * @param {Array} events Event list.
 * @param {Array<string>} acceptedCodes Event codes accepted for this column.
 * @returns {string} Formatted date or "-".
 */
function pickEventDate(events, acceptedCodes) {
    const list = Array.isArray(events) ? events : [];
    const codes = new Set((acceptedCodes || []).map((x) => String(x).trim().toUpperCase()));

    const match = list
        .filter((ev) => codes.has(getEventCode(ev)))
        .sort((a, b) => safeTime(b?.Timestamp ?? b?.timestamp) - safeTime(a?.Timestamp ?? a?.timestamp))[0];

    const timestamp = match?.Timestamp ?? match?.timestamp;

    return timestamp ? fmtDateOnly(timestamp) : "-";
}

/**
 * Reads nested values from object using paths like:
 * - Metadata.container_size
 * - metadata.container_type
 *
 * @param {object} obj Source object.
 * @param {Array<string>} paths Candidate paths.
 * @returns {any} First found value or null.
 */
function getAny(obj, paths) {
    for (const path of paths) {
        const parts = String(path).split(".");
        let cur = obj;
        let ok = true;

        for (const part of parts) {
            if (cur && Object.prototype.hasOwnProperty.call(cur, part)) {
                cur = cur[part];
            } else {
                ok = false;
                break;
            }
        }

        if (ok && cur != null) return cur;
    }

    return null;
}

/**
 * Builds a compact container badge.
 *
 * Existing providers may expose:
 * - container_size
 * - container_type
 *
 * OpenSyllo may expose:
 * - equipment_iso_code, e.g. 22G1
 * - container_type
 *
 * @param {object} item Tracking item.
 * @returns {string} Badge text or empty string.
 */
function buildContainerBadge(item) {
    const containerSize = getAny(item, ["Metadata.container_size", "metadata.container_size"]);
    const containerType = getAny(item, ["Metadata.container_type", "metadata.container_type"]);

    const equipmentIsoCode = getAny(item, ["Metadata.equipment_iso_code", "metadata.equipment_iso_code"]);
    const equipmentIsoCodeCamel = getAny(item, ["Metadata.equipmentIsoCode", "metadata.equipmentIsoCode"]);

    const sizeNumber = Number(containerSize);
    const size = Number.isFinite(sizeNumber) && sizeNumber > 0 ? String(sizeNumber) : "";

    const isoCode = String(equipmentIsoCode ?? equipmentIsoCodeCamel ?? "").trim();
    const type = String(containerType ?? "").trim();

    const firstPart = size || isoCode;

    if (!firstPart && !type) return "";

    return `${firstPart || "?"} ${type || ""}`.trim();
}